import React, { useState, useEffect } from 'react';
import { 
  getStudentRatings, 
  getStudentRatingAggregate,
  formatRating,
  getRatingColor
} from '../services/ratingService';

const StudentRatingDisplay = ({ 
  studentId, 
  showFullDetails = true,
  maxReviews = 5 
}) => {
  const [ratings, setRatings] = useState([]);
  const [aggregate, setAggregate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAllReviews, setShowAllReviews] = useState(false);

  useEffect(() => {
    if (studentId) {
      loadRatingData();
    }
  }, [studentId]);

  const loadRatingData = async () => {
    try {
      setLoading(true);
      const [ratingsData, aggregateData] = await Promise.all([
        getStudentRatings(studentId, showAllReviews ? 50 : maxReviews),
        getStudentRatingAggregate(studentId)
      ]);
      
      setRatings(ratingsData);
      setAggregate(aggregateData);
    } catch (error) {
      console.error('Error loading rating data:', error);
      setError('Failed to load ratings');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Recently';
    
    try {
      const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return 'Recently';
    }
  };

  const renderStars = (rating, size = '16px') => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
        {/* Full stars */}
        {Array(fullStars).fill(0).map((_, i) => (
          <span key={`full-${i}`} style={{ color: '#fbbf24', fontSize: size }}>
            <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
              <polygon points="12,2 15.09,8.26 22,9 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9 8.91,8.26"/>
            </svg>
          </span>
        ))}
        
        {/* Half star */}
        {hasHalfStar && (
          <span style={{ color: '#fbbf24', fontSize: size }}>
            <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
              <polygon points="12,2 15.09,8.26 22,9 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9 8.91,8.26"/>
            </svg>
          </span>
        )}
        
        {/* Empty stars */}
        {Array(emptyStars).fill(0).map((_, i) => (
          <span key={`empty-${i}`} style={{ color: '#e5e7eb', fontSize: size }}>
            <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
              <polygon points="12,2 15.09,8.26 22,9 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9 8.91,8.26"/>
            </svg>
          </span>
        ))}
      </div>
    );
  };

  const renderCategoryBar = (category, value, label) => {
    const percentage = (value / 5) * 100;
    
    return (
      <div style={{ marginBottom: '12px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '4px'
        }}>
          <span style={{ fontSize: '14px', fontWeight: '500' }}>{label}</span>
          <span style={{ fontSize: '14px', color: '#666' }}>{formatRating(value)}</span>
        </div>
        <div style={{
          height: '8px',
          backgroundColor: '#f1f5f9',
          borderRadius: '4px',
          overflow: 'hidden'
        }}>
          <div
            style={{
              height: '100%',
              width: `${percentage}%`,
              backgroundColor: getRatingColor(value),
              borderRadius: '4px',
              transition: 'width 0.3s ease'
            }}
          />
        </div>
      </div>
    );
  };

  const renderRatingDistribution = () => {
    if (!aggregate || !aggregate.ratingDistribution) return null;

    const { ratingDistribution, totalRatings } = aggregate;
    
    return (
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ 
          marginBottom: '16px',
          fontSize: '16px',
          fontWeight: 'bold',
          color: '#1a1a1a'
        }}>
          Rating Distribution
        </h4>
        
        {[5, 4, 3, 2, 1].map(star => {
          const count = ratingDistribution[star] || 0;
          const percentage = totalRatings > 0 ? (count / totalRatings) * 100 : 0;
          
          return (
            <div key={star} style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              marginBottom: '8px'
            }}>
              <span style={{ 
                fontSize: '14px', 
                width: '20px',
                fontWeight: '500'
              }}>
                {star}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" style={{ color: '#fbbf24' }}>
                  <polygon points="12,2 15.09,8.26 22,9 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9 8.91,8.26"/>
                </svg>
              </span>
              <div style={{
                flex: 1,
                height: '8px',
                backgroundColor: '#f1f5f9',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div
                  style={{
                    height: '100%',
                    width: `${percentage}%`,
                    backgroundColor: '#fbbf24',
                    borderRadius: '4px',
                    transition: 'width 0.3s ease'
                  }}
                />
              </div>
              <span style={{ 
                fontSize: '12px', 
                color: '#666',
                width: '30px',
                textAlign: 'right'
              }}>
                {count}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        textAlign: 'center'
      }}>
        <div style={{ color: '#666' }}>Loading ratings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        textAlign: 'center'
      }}>
        <div style={{ color: '#dc2626' }}>{error}</div>
      </div>
    );
  }

  if (!aggregate || aggregate.totalRatings === 0) {
    return (
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        textAlign: 'center'
      }}>
        <div style={{ 
          fontSize: '24px', 
          marginBottom: '16px',
          color: '#f59e0b',
          letterSpacing: '2px'
        }}>
          ★
        </div>
        <h3 style={{ 
          marginBottom: '8px',
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#1a1a1a'
        }}>
          No Ratings Yet
        </h3>
        <p style={{ color: '#666', margin: 0 }}>
          This student hasn't received any ratings from companies yet.
        </p>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      overflow: 'hidden'
    }}>
      {/* Rating Summary */}
      <div style={{ padding: '24px', borderBottom: '1px solid #f3f4f6' }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '20px',
          marginBottom: '20px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: '48px', 
              fontWeight: 'bold',
              color: getRatingColor(aggregate.averageRating),
              lineHeight: 1
            }}>
              {formatRating(aggregate.averageRating)}
            </div>
            <div style={{ marginTop: '8px' }}>
              {renderStars(aggregate.averageRating, '20px')}
            </div>
            <div style={{ 
              fontSize: '14px', 
              color: '#666',
              marginTop: '4px'
            }}>
              {aggregate.totalRatings} review{aggregate.totalRatings !== 1 ? 's' : ''}
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              <div style={{
                padding: '16px',
                backgroundColor: '#f0fdf4',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: 'bold',
                  color: '#15803d',
                  marginBottom: '4px'
                }}>
                  {aggregate.wouldWorkAgainPercentage}%
                </div>
                <div style={{ fontSize: '14px', color: '#166534' }}>
                  Would work again
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Category Ratings */}
        {showFullDetails && aggregate.categoryAverages && (
          <div>
            <h4 style={{ 
              marginBottom: '16px',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#1a1a1a'
            }}>
              Skill Breakdown
            </h4>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px'
            }}>
              <div>
                {renderCategoryBar('technicalSkills', aggregate.categoryAverages.technicalSkills, 'Technical Skills')}
                {renderCategoryBar('communication', aggregate.categoryAverages.communication, 'Communication')}
              </div>
              <div>
                {renderCategoryBar('workQuality', aggregate.categoryAverages.workQuality, 'Work Quality')}
                {renderCategoryBar('timeliness', aggregate.categoryAverages.timeliness, 'Timeliness')}
              </div>
              <div>
                {renderCategoryBar('professionalism', aggregate.categoryAverages.professionalism, 'Professionalism')}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Rating Distribution (full details only) */}
      {showFullDetails && (
        <div style={{ padding: '24px', borderBottom: '1px solid #f3f4f6' }}>
          {renderRatingDistribution()}
        </div>
      )}

      {/* Individual Reviews */}
      <div style={{ padding: '24px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h4 style={{ 
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#1a1a1a',
            margin: 0
          }}>
            Recent Reviews
          </h4>
          
          {ratings.length > maxReviews && !showAllReviews && (
            <button
              onClick={() => {
                setShowAllReviews(true);
                loadRatingData();
              }}
              style={{
                backgroundColor: 'transparent',
                color: '#6366f1',
                border: 'none',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Show all reviews
            </button>
          )}
        </div>

        {ratings.length === 0 ? (
          <p style={{ color: '#666', fontStyle: 'italic' }}>
            No detailed reviews available.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {ratings.map((rating, index) => (
              <div 
                key={rating.id || index}
                style={{
                  padding: '20px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #f1f5f9'
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  marginBottom: '12px'
                }}>
                  <div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      marginBottom: '4px'
                    }}>
                      {renderStars(rating.overallRating)}
                      <span style={{ 
                        fontSize: '14px', 
                        fontWeight: 'bold',
                        color: '#1a1a1a'
                      }}>
                        {formatRating(rating.overallRating)}/5
                      </span>
                    </div>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                      {rating.companyName} • {rating.projectTitle}
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {formatDate(rating.createdAt)}
                  </div>
                </div>

                {rating.review && (
                  <p style={{ 
                    color: '#374151',
                    lineHeight: 1.6,
                    margin: '0 0 12px 0',
                    fontSize: '14px'
                  }}>
                    "{rating.review}"
                  </p>
                )}

                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  {rating.wouldWorkAgain && (
                    <span style={{
                      backgroundColor: '#d1fae5',
                      color: '#065f46',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      Would work again
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentRatingDisplay; 